/**
 * Module dependencies.
 */

import { AnimationController } from 'src/components/core/animations/animation-controller';
import { CardGradient } from 'src/components/core/cards/card-gradient';
import { FadeInUp } from 'src/components/core/animations/fade-in-up';
import { Grid, SubGrid } from 'src/components/core/layout/grid-content';
import { Section } from 'src/components/core/layout/section';
import { Text } from 'src/components/core/text';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  offers: Offers[];
};

/**
 * Export `Offers` type.
 */

export type Offers = {
  description: string;
  limitDate: string;
  title: string;
};

/**
 * `Title` styled component.
 */

const Title = styled(Text).attrs({ variant: 'labelL', weight: 600 })`
  background: var(--gradient-primary--15-deg);
  -webkit-background-clip: text;
  background-clip: text;
  color: var(--color-primary);
  -webkit-text-fill-color: transparent;
`;

/**
 * `Description` styled component.
 */

const Description = styled(Text).attrs({ as: 'p', variant: 'bodyL' })`
  padding-top: var(--space-xs);
`;

/**
 * Export `OffersSection` component.
 */

export const OffersSection = ({ offers }: Props) => {
  const [validOffers, setValidOffers] = useState<Offers[]>([]);

  useEffect(() => {
    const filteredOffers = offers.filter(offer => new Date() < new Date(offer.limitDate));
    setValidOffers(filteredOffers);
  }, [offers]);

  if (validOffers.length === 0) {
    return null;
  }

  return (
    <Section>
      <Grid>
        <SubGrid>
          <AnimationController>
            {validOffers.map((offer, index) => (
              <FadeInUp key={index} triggerInView>
                <CardGradient style={{ padding: 'var(--space-ms) var(--space-sm)' }}>
                  <Title>{offer.title}</Title>
                  <Description>{offer.description}</Description>
                </CardGradient>
              </FadeInUp>
            ))}
          </AnimationController>
        </SubGrid>
      </Grid>
    </Section>
  );
};
